import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { Loader2, Plus, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation } from "react-router";

import {
  parseBrandAssistantActivityUpdatePart,
  type BrandAssistantActivityUpdateData,
} from "@runcheetah/shared";
import { Button } from "@runcheetah/ui";

import {
  appendAssistantLiveActivityUpdate,
  AssistantChatPanel,
  getLatestAssistantMessageAfterLatestUser,
  getPersistedAssistantStreamActivityUpdates,
  hasActiveAssistantMessagePart,
  hasActivePersistedAssistantStream,
  shouldRenderAssistantStructuredExtras,
} from "@/components/chat/assistant-chat";
import { AssistantAudienceProfileCreationPreviews } from "@/components/chat/assistant-audience-profile-creation-previews";
import { AssistantBrandProfileEditPreviews } from "@/components/chat/assistant-brand-profile-edit-previews";
import { AssistantLiveActivity } from "@/components/chat/assistant-live-activity";
import { useRouteBrand } from "@/hooks/use-route-brand";
import { useMarkBrandAssistantChatSeen } from "@/hooks/use-mark-brand-assistant-chat-seen";
import {
  getAssistantBrandProfileEditPreviewReferences,
  getAssistantBrandProfileEditPreviewResolutionStatuses,
} from "@/lib/assistant-brand-profile-edit-previews";
import {
  getAssistantAudienceProfileCreationPreviewReferences,
  isAssistantAudienceProfileCreationProposalStale,
} from "@/lib/assistant-audience-profile-creation-previews";
import { getBrandAssistantCurrentPageContext } from "@/lib/assistant-current-page-context";
import { ASSISTANT_ROUTE_CATALOG } from "@/lib/app-route-manifest";
import {
  buildBrandAssistantChatPath,
  buildBrandAssistantNewChatPath,
  type BrandAssistantCreatedChatTransition,
  type BrandAssistantChatSummary,
  getBrandAssistantChatRuntimeKey,
  getLatestBrandAssistantUserMessage,
  getRetryableBrandAssistantUserMessage,
  isBrandAssistantResponsePersisted,
  PENDING_BRAND_ASSISTANT_CHAT_ID,
  resolveBrandAssistantChatSnapshotMessages,
} from "@/lib/brand-assistant-chat-history";
import {
  buildBrandAssistantSendMessagesRequestBody,
  buildBrandAssistantSendRequestOptionsBody,
  getBrandAssistantSendRequestContext,
} from "@/lib/brand-assistant-chat-request";
import { trpc } from "@/lib/trpc";

const EMPTY_MESSAGES: UIMessage[] = [];
const SIDEBAR_CHAT_PAGE_SIZE = 3;
const CHAT_RESPONSE_PERSISTENCE_ATTEMPTS = 12;
const CHAT_RESPONSE_PERSISTENCE_INTERVAL_MS = 250;

export function BrandAssistantDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const intl = useIntl();
  const location = useLocation();
  const utils = trpc.useUtils();
  const { brandId, brand, onboarding } = useRouteBrand();
  const [draft, setDraft] = useState("");
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [isPendingDraftChat, setIsPendingDraftChat] = useState(false);
  const [pendingChatRuntimeKey, setPendingChatRuntimeKey] = useState(() => crypto.randomUUID());
  const [createdChatTransition, setCreatedChatTransition] =
    useState<BrandAssistantCreatedChatTransition | null>(null);
  const [submissionError, setSubmissionError] = useState<Error | null>(null);
  const [isFinalizingResponse, setIsFinalizingResponse] = useState(false);
  const [liveActivityUpdates, setLiveActivityUpdates] = useState<
    BrandAssistantActivityUpdateData[]
  >([]);
  const lastRequestErrorRef = useRef<Error | null>(null);
  const lastResetChatRef = useRef<string | null>(null);
  const firstAssetReady = onboarding?.completion.firstAssetReady === true;
  const currentPage = useMemo(
    () =>
      getBrandAssistantCurrentPageContext({
        brandId,
        pathname: location.pathname,
      }),
    [brandId, location.pathname],
  );
  const isPulsePage = currentPage?.routeId === "brand-pulse";
  const shouldShow = Boolean(brandId && brand && firstAssetReady && currentPage && !isPulsePage);

  const chatListQuery = trpc.brandAssistantChat.list.useQuery(
    { brandId: brandId ?? PENDING_BRAND_ASSISTANT_CHAT_ID },
    {
      enabled: shouldShow,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  );
  const createChatMutation = trpc.brandAssistantChat.create.useMutation();
  const latestChatId = chatListQuery.data?.chats[0]?.id ?? null;
  const activeChatId = isPendingDraftChat ? null : (selectedChatId ?? latestChatId);
  const chatQuery = trpc.brandAssistantChat.get.useQuery(
    {
      brandId: brandId ?? PENDING_BRAND_ASSISTANT_CHAT_ID,
      chatId: activeChatId ?? PENDING_BRAND_ASSISTANT_CHAT_ID,
    },
    {
      enabled: shouldShow && open && Boolean(activeChatId),
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_URL ?? ""}/api/chat/brand-assistant`,
        credentials: "include",
        prepareSendMessagesRequest({ messages, trigger, body }) {
          const requestContext = getBrandAssistantSendRequestContext(body);
          return {
            body: buildBrandAssistantSendMessagesRequestBody({
              brandId: brandId ?? PENDING_BRAND_ASSISTANT_CHAT_ID,
              chatId: requestContext?.chatId ?? activeChatId ?? PENDING_BRAND_ASSISTANT_CHAT_ID,
              routeCatalog: ASSISTANT_ROUTE_CATALOG,
              currentPage,
              trigger,
              messages,
            }),
          };
        },
      }),
    [activeChatId, brandId, currentPage],
  );

  const chatData = chatQuery.data as
    | {
        chat: {
          id: string;
          messages: UIMessage[];
          assistantContentVersion: number;
          seenAssistantContentVersion: number;
        } | null;
      }
    | undefined;
  const activeChat = chatData?.chat ?? null;
  useMarkBrandAssistantChatSeen({
    brandId: brandId ?? PENDING_BRAND_ASSISTANT_CHAT_ID,
    chat: activeChat,
    enabled: open,
  });
  const chatRuntimeKey = getBrandAssistantChatRuntimeKey({
    chatId: activeChatId ?? undefined,
    routeChatIsPending: !activeChatId,
    pendingLocationKey: pendingChatRuntimeKey,
    createdChatTransition,
  });
  const { messages, sendMessage, regenerate, status, stop, error, setMessages, clearError } =
    useChat({
      id: `brand-assistant-widget-${brandId ?? "pending"}-${chatRuntimeKey}`,
      messages: activeChat?.messages ?? EMPTY_MESSAGES,
      transport,
      experimental_throttle: 250,
      onError(requestError) {
        lastRequestErrorRef.current = requestError;
      },
      onData(dataPart) {
        const update = parseBrandAssistantActivityUpdatePart(dataPart)?.data;
        if (update) {
          setLiveActivityUpdates((currentUpdates) =>
            appendAssistantLiveActivityUpdate(currentUpdates, update),
          );
        }
      },
    });
  const messagesRef = useRef(messages);
  const isBusy = status === "submitted" || status === "streaming";
  const hasPersistedStream = hasActivePersistedAssistantStream(messages);
  const activeChatHasPersistedStream = hasActivePersistedAssistantStream(
    activeChat?.messages ?? EMPTY_MESSAGES,
  );
  const effectiveStatus =
    isBusy || hasPersistedStream || isFinalizingResponse ? "streaming" : status;
  const isChatBusy = isBusy || hasPersistedStream || isFinalizingResponse;
  const latestAssistantMessage = useMemo(
    () => getLatestAssistantMessageAfterLatestUser(messages),
    [messages],
  );
  const persistedStreamActivityUpdates = useMemo(
    () => getPersistedAssistantStreamActivityUpdates(messages),
    [messages],
  );
  const isPreparingChat =
    open &&
    (chatListQuery.isPending ||
      createChatMutation.isPending ||
      (Boolean(activeChatId) && chatQuery.isPending && !activeChat));

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!isBusy) {
      setLiveActivityUpdates([]);
    }
  }, [isBusy]);

  useEffect(() => {
    if (!brandId || !activeChatId || !hasPersistedStream) {
      return;
    }

    const interval = window.setInterval(() => {
      void utils.brandAssistantChat.get.invalidate({ brandId, chatId: activeChatId });
      void utils.brandAssistantChat.list.invalidate({ brandId });
    }, 1_000);

    return () => window.clearInterval(interval);
  }, [
    activeChatId,
    brandId,
    hasPersistedStream,
    utils.brandAssistantChat.get,
    utils.brandAssistantChat.list,
  ]);

  useEffect(() => {
    if (isBusy || !activeChat || (!hasPersistedStream && !activeChatHasPersistedStream)) {
      return;
    }

    setMessages(
      resolveBrandAssistantChatSnapshotMessages({
        currentMessages: messagesRef.current,
        nextMessages: activeChat.messages as UIMessage[],
      }),
    );
  }, [activeChat, activeChatHasPersistedStream, hasPersistedStream, isBusy, setMessages]);

  useEffect(() => {
    if (
      isBusy ||
      !activeChat ||
      createdChatTransition?.chatId === activeChat.id ||
      activeChat.id === lastResetChatRef.current
    ) {
      return;
    }

    lastResetChatRef.current = activeChat.id;
    setMessages(activeChat.messages as UIMessage[]);
    setDraft("");
    setLiveActivityUpdates([]);
    setSubmissionError(null);
    clearError();
  }, [activeChat, clearError, createdChatTransition?.chatId, isBusy, setMessages]);

  if (!shouldShow || !open || !brandId || !currentPage) {
    return null;
  }

  const createChat = () => {
    lastResetChatRef.current = null;
    setSelectedChatId(null);
    setIsPendingDraftChat(true);
    setPendingChatRuntimeKey(crypto.randomUUID());
    setCreatedChatTransition(null);
    setMessages(EMPTY_MESSAGES);
    setDraft("");
    setLiveActivityUpdates([]);
    setSubmissionError(null);
    clearError();
    onOpenChange(true);
  };

  const refreshActiveChat = async (chatId: string): Promise<{ messages: unknown[] } | null> => {
    const [, refreshedChat] = await Promise.all([
      utils.brandAssistantChat.list.invalidate({ brandId }),
      utils.brandAssistantChat.get.fetch({ brandId, chatId }),
    ]);
    await utils.brandAssistantChat.get.invalidate({ brandId, chatId });
    if (refreshedChat.chat) {
      updateCachedChatListItem(refreshedChat.chat);
    }
    return refreshedChat.chat
      ? ({ messages: refreshedChat.chat.messages } as { messages: unknown[] })
      : null;
  };

  const waitForPersistedResponse = async (chatId: string, userMessageId: string) => {
    let latestChat: Awaited<ReturnType<typeof refreshActiveChat>> = null;
    for (let attempt = 0; attempt < CHAT_RESPONSE_PERSISTENCE_ATTEMPTS; attempt += 1) {
      latestChat = await refreshActiveChat(chatId);
      if (
        latestChat &&
        isBrandAssistantResponsePersisted({
          messages: latestChat.messages as UIMessage[],
          userMessageId,
        })
      ) {
        return latestChat;
      }

      if (attempt < CHAT_RESPONSE_PERSISTENCE_ATTEMPTS - 1) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, CHAT_RESPONSE_PERSISTENCE_INTERVAL_MS),
        );
      }
    }
    return latestChat;
  };

  const updateCachedChatListItem = (chat: BrandAssistantChatSummary) => {
    const updateList = (input: { brandId: string; limit?: number }) => {
      utils.brandAssistantChat.list.setData(input, (current) =>
        current
          ? {
              ...current,
              chats: current.chats.map((item) =>
                item.id === chat.id ? { ...item, ...chat } : item,
              ),
            }
          : current,
      );
    };

    updateList({ brandId });
    updateList({ brandId, limit: SIDEBAR_CHAT_PAGE_SIZE });
  };

  const insertOptimisticChatListItem = (
    chat: NonNullable<Awaited<ReturnType<typeof createChatMutation.mutateAsync>>["chat"]>,
  ) => {
    const updateList = (input: { brandId: string; limit?: number }) => {
      utils.brandAssistantChat.list.setData(input, (current) => {
        if (!current) {
          return { chats: [chat], hasUnseenAssistantContent: false, nextCursor: null };
        }

        return {
          ...current,
          chats: [chat, ...current.chats.filter((item) => item.id !== chat.id)],
        };
      });
    };

    updateList({ brandId });
    updateList({ brandId, limit: SIDEBAR_CHAT_PAGE_SIZE });
  };

  const seedActiveChat = (
    chat: NonNullable<Awaited<ReturnType<typeof createChatMutation.mutateAsync>>["chat"]>,
  ) => {
    utils.brandAssistantChat.get.setData({ brandId, chatId: chat.id }, { chat });
  };

  const sendPrompt = async (prompt: string) => {
    if (isChatBusy) {
      return;
    }

    const userMessage = {
      id: `msg_${crypto.randomUUID()}`,
      role: "user" as const,
      parts: [{ type: "text" as const, text: prompt }],
    };
    let sendChatId = activeChatId;
    if (!sendChatId) {
      let createdChat: Awaited<ReturnType<typeof createChatMutation.mutateAsync>>;
      try {
        createdChat = await createChatMutation.mutateAsync({
          brandId,
          initialMessage: userMessage,
        });
      } catch (createError) {
        setDraft(prompt);
        setSubmissionError(
          createError instanceof Error
            ? createError
            : new Error(
                intl.formatMessage({
                  id: "common.somethingWentWrong",
                  defaultMessage: "Something went wrong.",
                }),
              ),
        );
        return;
      }

      sendChatId = createdChat.chat.id;
      setCreatedChatTransition({ chatId: sendChatId, runtimeKey: chatRuntimeKey });
      insertOptimisticChatListItem(createdChat.chat);
      seedActiveChat(createdChat.chat);
      setMessages([userMessage as UIMessage]);
      setSelectedChatId(sendChatId);
      setIsPendingDraftChat(false);

      setSubmissionError(null);
      clearError();
      lastRequestErrorRef.current = null;
      await regenerate({
        messageId: userMessage.id,
        body: buildBrandAssistantSendRequestOptionsBody({ chatId: sendChatId }),
      });
      const requestFailed = lastRequestErrorRef.current !== null;
      setIsFinalizingResponse(!requestFailed);
      try {
        const refreshedChat = requestFailed
          ? await refreshActiveChat(sendChatId)
          : await waitForPersistedResponse(sendChatId, userMessage.id);
        if (
          !requestFailed &&
          refreshedChat &&
          isBrandAssistantResponsePersisted({
            messages: refreshedChat.messages as UIMessage[],
            userMessageId: userMessage.id,
          })
        ) {
          lastResetChatRef.current = sendChatId;
          setCreatedChatTransition(null);
        } else if (!requestFailed) {
          setSubmissionError(
            new Error(
              intl.formatMessage({
                id: "assistantChat.responseIncomplete",
                defaultMessage: "Cheetah didn't finish this response.",
              }),
            ),
          );
        }
      } catch {
        // The persisted user turn remains available for an explicit retry.
      } finally {
        setIsFinalizingResponse(false);
      }
      return;
    }

    setSubmissionError(null);
    clearError();
    lastRequestErrorRef.current = null;
    try {
      await sendMessage(userMessage, {
        body: buildBrandAssistantSendRequestOptionsBody({ chatId: sendChatId }),
      });
      await refreshActiveChat(sendChatId);
    } catch {
      // The stream error remains visible and the persisted user turn can be retried.
    }
  };

  const displayedError = submissionError ?? error;
  const retryableUserMessage = !isChatBusy
    ? displayedError
      ? getLatestBrandAssistantUserMessage(messages)
      : getRetryableBrandAssistantUserMessage(messages)
    : null;
  const retryResponse = async () => {
    if (!retryableUserMessage || !activeChatId || isChatBusy) {
      return;
    }

    setSubmissionError(null);
    clearError();
    lastRequestErrorRef.current = null;
    await regenerate({
      messageId: retryableUserMessage.id,
      body: buildBrandAssistantSendRequestOptionsBody({ chatId: activeChatId }),
    });
    const requestFailed = lastRequestErrorRef.current !== null;
    setIsFinalizingResponse(!requestFailed);
    try {
      const refreshedChat = requestFailed
        ? await refreshActiveChat(activeChatId)
        : await waitForPersistedResponse(activeChatId, retryableUserMessage.id);
      if (
        !requestFailed &&
        refreshedChat &&
        isBrandAssistantResponsePersisted({
          messages: refreshedChat.messages as UIMessage[],
          userMessageId: retryableUserMessage.id,
        })
      ) {
        lastResetChatRef.current = activeChatId;
        setCreatedChatTransition(null);
      } else if (!requestFailed) {
        setSubmissionError(
          new Error(
            intl.formatMessage({
              id: "assistantChat.responseIncomplete",
              defaultMessage: "Cheetah didn't finish this response.",
            }),
          ),
        );
      }
    } catch {
      // Keep the retry action available for the persisted user turn.
    } finally {
      setIsFinalizingResponse(false);
    }
  };

  const profilePreviewResolutionStatuses =
    getAssistantBrandProfileEditPreviewResolutionStatuses(messages);
  const activityUpdates =
    liveActivityUpdates.length > 0 ? liveActivityUpdates : persistedStreamActivityUpdates;
  const liveActivity =
    isChatBusy && activityUpdates.length > 0 ? (
      <AssistantLiveActivity updates={activityUpdates} />
    ) : null;

  return (
    <div className="flex h-full min-h-0 w-[min(480px,42vw)] shrink-0 border-l border-border bg-card">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {isPreparingChat ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          </div>
        ) : (
          <AssistantChatPanel
            messages={messages}
            status={effectiveStatus}
            draft={draft}
            error={displayedError}
            topLabel={
              <span className="flex min-w-0 flex-col gap-1 normal-case tracking-normal">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-foreground/48">
                  <FormattedMessage
                    id="brandAssistantWidget.panelLabel"
                    defaultMessage="Assistant"
                  />
                </span>
                <span className="truncate text-[11px] font-medium text-muted-foreground">
                  <FormattedMessage
                    id="brandAssistantWidget.context"
                    defaultMessage="Using {page} context"
                    values={{ page: currentPage.label }}
                  />
                </span>
              </span>
            }
            topActions={
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-full"
                  onClick={createChat}
                  aria-label={intl.formatMessage({
                    id: "brandAssistantWidget.newChat",
                    defaultMessage: "New chat",
                  })}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
                {activeChatId || isPendingDraftChat ? (
                  <Button
                    asChild
                    size="sm"
                    variant="ghost"
                    className="h-8 rounded-full px-3 text-xs"
                  >
                    <Link
                      to={
                        activeChatId
                          ? buildBrandAssistantChatPath(brandId, activeChatId)
                          : buildBrandAssistantNewChatPath(brandId)
                      }
                    >
                      <FormattedMessage id="brandAssistantWidget.openPulse" defaultMessage="Open" />
                    </Link>
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-full"
                  onClick={() => onOpenChange(false)}
                  aria-label={intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </>
            }
            showThreadToolbar={false}
            className="min-h-0 flex-1 bg-transparent shadow-none"
            contentClassName="flex flex-col px-4 py-4"
            messageListClassName="mt-auto"
            composerContainerClassName="border-t border-border/70 bg-card px-4 py-3"
            inputClassName="text-sm"
            renderAssistantMessageExtra={(message) => {
              const shouldRenderStructuredExtras = shouldRenderAssistantStructuredExtras({
                isBusy: isChatBusy,
                messageId: message.id,
                latestAssistantMessageId: latestAssistantMessage?.id ?? null,
                hasActiveMessagePart: hasActiveAssistantMessagePart(message),
              });
              const profileEditPreviews = shouldRenderStructuredExtras
                ? getAssistantBrandProfileEditPreviewReferences(message).filter(
                    (reference) => reference.confirmationSource !== "chatbot_onboarding",
                  )
                : [];
              const audienceProfileCreationPreviews = shouldRenderStructuredExtras
                ? getAssistantAudienceProfileCreationPreviewReferences(message)
                : [];

              if (
                profileEditPreviews.length === 0 &&
                audienceProfileCreationPreviews.length === 0
              ) {
                return null;
              }

              return (
                <div className="space-y-4">
                  <AssistantBrandProfileEditPreviews
                    brandId={brandId}
                    chatId={activeChatId}
                    references={profileEditPreviews}
                    resolutionStatuses={profilePreviewResolutionStatuses}
                  />
                  <AssistantAudienceProfileCreationPreviews
                    brandId={brandId}
                    sourceChatId={activeChatId}
                    references={audienceProfileCreationPreviews}
                    isStale={isAssistantAudienceProfileCreationProposalStale(messages, message.id)}
                  />
                </div>
              );
            }}
            emptyState={
              <div className="flex h-full min-h-52 flex-col items-center justify-center px-4 text-center">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary">
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </div>
                <h2 className="text-base font-semibold text-foreground">
                  <FormattedMessage
                    id="brandAssistantWidget.emptyTitle"
                    defaultMessage="Ask about this page"
                  />
                </h2>
                <p className="mt-2 text-sm leading-5 text-muted-foreground">
                  <FormattedMessage
                    id="brandAssistantWidget.emptyDescription"
                    defaultMessage="I can use the current {page} context and keep this chat in Pulse history."
                    values={{ page: currentPage.label }}
                  />
                </p>
              </div>
            }
            placeholder={intl.formatMessage({
              id: "brandAssistantWidget.placeholder",
              defaultMessage: "Ask about this page...",
            })}
            runtimeActivity={liveActivity}
            onDraftChange={setDraft}
            onSubmit={sendPrompt}
            onStop={stop}
            onClearError={() => {
              setSubmissionError(null);
              clearError();
            }}
            onRetry={retryableUserMessage ? retryResponse : undefined}
            isRetrying={isBusy}
            disableRuntimeThoughts
            showActivityParts={false}
          />
        )}
      </div>
    </div>
  );
}
